import React, { useEffect, useRef } from 'react';
import { Input, Select, Tooltip } from 'antd';
import type { GetRef } from 'antd';
import { RotateCcw, Trash2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { HtsInputType, HtsYesNoAnswer, MAX_ANSWER_MESSAGE_LENGTH } from '@evaltick/shared';
import type { HtsFollowUpExchange } from '@/services/api';
import type { DisplayQuestion } from './HtsQuestionnaire';
import { Button } from '@/components/Button';
import { useLiquidGlass } from '@/hooks';
import { FORM_GLASS_SETTINGS } from '@/constants';
import { isNoneOfTheAboveOption } from '@/utils/htsCheckboxOptions';
import { CheckIcon } from './CheckIcon';
import styles from './HtsQuestionnaire.module.scss';

/** One REPEATABLE_GROUP row — sub-field key to its (always scalar) value. */
type GroupRow = Record<string, string>;

/**
 * REPEATABLE_GROUP answers are compiled entirely on the frontend: the
 * backend never parses row structure, it just stores/passes through one
 * JSON-stringified string like any other TEXTAREA answer (see
 * apps/api/src/services/htsReadinessScoring.ts). Parsing failures (e.g. an
 * older plain-string answer) fall back to no rows rather than throwing.
 */
const parseGroupRows = (value: string | string[] | undefined): GroupRow[] => {
  if (typeof value !== 'string' || !value.trim()) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

// A row's CHECKBOXES sub-field stores its selected options as a
// JSON-stringified array within that one scalar row value (`GroupRow` is
// string-only, matching how the row array itself is stringified).
export const parseCheckboxRowValue = (value: string | undefined): string[] => {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

interface HtsQuestionCardProps {
  question: DisplayQuestion;
  isLast: boolean;
  answer: string | string[] | undefined;
  allAnswered: boolean;
  /** True once the current question itself has a complete answer — gates the "Next question" button. */
  currentAnswered: boolean;
  /** True once this question's answer has been persisted at least once (unlike `currentAnswered`, true for any local value, even mid-draft). Gates the "edit once" note. */
  hasSavedAnswer?: boolean;
  /** True whenever the live draft differs from what's persisted — first-time unsaved or an edit to a saved answer. Rendered as its own status line, separate from the "edit once" note. */
  hasUnsavedChanges?: boolean;
  /** The last value actually persisted for this question — what a "Revert" click restores `answer` to. Only meaningful (and only rendered) alongside `hasUnsavedChanges` and `hasSavedAnswer`. */
  lastSavedValue?: string | string[];
  /** True once this question's answer can no longer be changed — renders it read-only instead of editable. */
  locked?: boolean;
  /** Caption shown under a locked question's read-only value — defaults to the one-time-redo-exhausted message. */
  lockedCaption?: string;
  /** Show the "you can edit this once before it locks" note once answered — false for the write-once perfect-person probe, which has no redo. */
  showEditOnceNote?: boolean;
  /** Present from the second question on — steps back to the previous (now-editable) question. */
  onBack?: () => void;
  onChange: (value: string | string[]) => void;
  onNext: () => void;
  onSubmit: () => void;
  /**
   * The full AI follow-up conversation for this question, oldest round
   * first — every round already asked and answered, plus a still-pending one
   * (if any) as the last entry with `answer: null`. Rendered as one running
   * conversation so the candidate can always see everything they've already
   * answered, not just the latest question.
   */
  followUpHistory?: HtsFollowUpExchange[];
  /** In-progress draft for the still-pending entry (the last one, if its answer is null). */
  followUpAnswer?: string;
  onFollowUpAnswerChange?: (value: string) => void;
  /** True while a save/follow-up round-trip is in flight — disables Next/Submit. */
  saving?: boolean;
}

export const HtsQuestionCard: React.FC<HtsQuestionCardProps> = ({
  question,
  isLast,
  answer,
  allAnswered,
  currentAnswered,
  hasSavedAnswer,
  hasUnsavedChanges,
  lastSavedValue,
  locked,
  lockedCaption,
  showEditOnceNote = true,
  onBack,
  onChange,
  onNext,
  onSubmit,
  followUpHistory,
  followUpAnswer,
  onFollowUpAnswerChange,
  saving,
}) => {
  // Same real refractive glass as the auth form containers (AuthLayout's
  // .form) — and MainLayout's .headerBar / UserMenu's .menu — via the shared
  // FORM_GLASS_SETTINGS, rather than the flatter static @include glass-surface
  // approximation this card used before (which also didn't match its
  // border-radius: FORM_GLASS_SETTINGS sets 24px via the hook, not
  // --radius-lg's 12px).
  const glassRef = useLiquidGlass<HTMLDivElement>(FORM_GLASS_SETTINGS);

  // Stable React keys for REPEATABLE_GROUP rows. A row has no id of its own —
  // rows exist only inside the JSON-stringified answer, and adding one would
  // pollute the stored payload — and the array index can't serve, since
  // removing a row shifts every row after it onto its neighbour's key. This
  // parallel id list is padded as rows appear and spliced at the removed
  // index, so each row keeps its identity. Reset when the question changes.
  const rowKeysRef = useRef<{ questionId: string; keys: string[] }>({ questionId: question.id, keys: [] });
  const rowKeySeqRef = useRef(0);
  if (rowKeysRef.current.questionId !== question.id) {
    rowKeysRef.current = { questionId: question.id, keys: [] };
  }
  const syncRowKeys = (rowCount: number): string[] => {
    const { keys } = rowKeysRef.current;
    while (keys.length < rowCount) keys.push(`row-${rowKeySeqRef.current++}`);
    keys.length = rowCount;
    return keys;
  };

  const pendingScrollRowKeyRef = useRef<string | null>(null);
  const cardBodyRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const key = pendingScrollRowKeyRef.current;
    if (!key) return;
    pendingScrollRowKeyRef.current = null;
    cardBodyRef.current
      ?.querySelector(`[data-row-key="${key}"]`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [answer]);

  const history = followUpHistory ?? [];

  const historyGrowthRef = useRef<{ questionId: string; length: number }>({
    questionId: question.id,
    length: history.length,
  });
  useEffect(() => {
    const prev = historyGrowthRef.current;
    if (prev.questionId === question.id && history.length > prev.length) {
      cardBodyRef.current?.scrollTo({ top: cardBodyRef.current.scrollHeight, behavior: 'smooth' });
    }
    historyGrowthRef.current = { questionId: question.id, length: history.length };
  }, [question.id, history.length]);

  const lastExchange = history[history.length - 1];
  const pendingFollowUpQuestion = lastExchange && lastExchange.answer === null ? lastExchange.question : null;
  const answeredExchanges = pendingFollowUpQuestion ? history.slice(0, -1) : history;

  // Editing the parent answer invalidates the pending follow-up, so it stops
  // requiring an answer to it once the candidate has unsaved changes on the
  // parent value.
  const blockedOnFollowUp = !!pendingFollowUpQuestion && !hasUnsavedChanges && !followUpAnswer?.trim();

  // Not `autoFocus`: the textarea remounts whenever `hasUnsavedChanges` flips,
  // which would steal focus from the parent field mid-edit. Keyed on the
  // follow-up question instead, so focus lands once per new round.
  const followUpInputRef = useRef<GetRef<typeof Input.TextArea>>(null);
  const focusedFollowUpRef = useRef<{ questionId: string; question: string } | null>(null);
  useEffect(() => {
    if (!pendingFollowUpQuestion || hasUnsavedChanges) return;
    const focused = focusedFollowUpRef.current;
    if (focused?.questionId === question.id && focused.question === pendingFollowUpQuestion) return;
    focusedFollowUpRef.current = { questionId: question.id, question: pendingFollowUpQuestion };
    followUpInputRef.current?.focus();
  }, [question.id, pendingFollowUpQuestion, hasUnsavedChanges]);

  // Renders just the input widget for `question.inputType`, given an
  // explicit value/onChange pair.
  const renderFieldFor = (value: string | string[] | undefined, onValueChange: (v: string | string[]) => void) => {
    const stringValue = typeof value === 'string' ? value : undefined;
    const arrayValue = Array.isArray(value) ? value : [];

    switch (question.inputType) {
      case HtsInputType.TEXT:
        return (
          <Input
            value={stringValue ?? ''}
            onChange={(e) => onValueChange(e.target.value)}
            placeholder="Type your answer…"
            className={styles.fieldInput}
            size="large"
            autoFocus
          />
        );

      case HtsInputType.DATE:
        return (
          <input
            type="date"
            value={stringValue ?? ''}
            onChange={(e) => onValueChange(e.target.value)}
            className={styles.fieldDateNative}
            autoFocus
          />
        );

      case HtsInputType.SELECT:
        return (
          <Select
            value={stringValue || undefined}
            onChange={(v) => onValueChange(v)}
            placeholder="Choose one…"
            className={styles.fieldSelect}
            size="large"
            options={question.options?.map((o) => ({ value: o, label: o }))}
            autoFocus
          />
        );

      case HtsInputType.TEXTAREA:
        return (
          <Input.TextArea
            value={stringValue ?? ''}
            onChange={(e) => onValueChange(e.target.value)}
            placeholder="Type your answer…"
            className={styles.fieldTextarea}
            rows={5}
            maxLength={MAX_ANSWER_MESSAGE_LENGTH}
            autoFocus
          />
        );

      case HtsInputType.CHECKBOXES: {
        const noneOption = question.options?.find(isNoneOfTheAboveOption);
        const noneSelected = !!noneOption && arrayValue.includes(noneOption);

        return (
          <div className={styles.checkboxList}>
            {question.options?.map((opt) => {
              const selected = arrayValue.includes(opt);
              const isNoneOpt = opt === noneOption;
              const optionDisabled = noneSelected && !isNoneOpt;
              return (
                <button
                  key={opt}
                  type="button"
                  disabled={optionDisabled}
                  className={`${styles.checkOpt} ${selected ? styles.checkOptSelected : ''}`}
                  onClick={() => {
                    if (isNoneOpt) {
                      onValueChange(selected ? [] : [opt]);
                      return;
                    }
                    onValueChange(
                      selected ? arrayValue.filter((x) => x !== opt) : [...arrayValue, opt],
                    );
                  }}
                >
                  <span className={`${styles.checkBox} ${selected ? styles.checkBoxSelected : ''}`}>
                    {selected && <CheckIcon size={14} />}
                  </span>
                  <span>{opt}</span>
                </button>
              );
            })}
          </div>
        );
      }

      case HtsInputType.YES_NO:
        return (
          <div className={styles.yesnoBtns}>
            <button
              type="button"
              className={`${styles.yesnoBtn} ${value === HtsYesNoAnswer.YES ? styles.yesnoBtnActive : ''}`}
              onClick={() => onValueChange(HtsYesNoAnswer.YES)}
            >
              Yes
            </button>
            <button
              type="button"
              className={`${styles.yesnoBtn} ${value === HtsYesNoAnswer.NO ? styles.yesnoBtnActive : ''}`}
              onClick={() => onValueChange(HtsYesNoAnswer.NO)}
            >
              No
            </button>
          </div>
        );

      case HtsInputType.REPEATABLE_GROUP: {
        const rows = parseGroupRows(value);
        const fields = question.groupFields ?? [];
        // "[]" is the explicit "none of these apply to me" answer — distinct
        // from an untouched field (undefined/""), which isn't answered yet.
        const explicitlyNone = value === '[]';
        // Show the first row's fields immediately rather than requiring an
        // "Add" click — it only becomes a real, stored row once a field in it
        // is touched (see updateRow below). "None" and the row coexist on
        // screen; only "None"'s checked state reflects which one is the answer.
        const displayRows = rows.length > 0 ? rows : [{}];
        const rowKeys = syncRowKeys(displayRows.length);

        const updateRow = (index: number, key: string, fieldValue: string) => {
          const nextRows = [...displayRows];
          nextRows[index] = { ...nextRows[index], [key]: fieldValue };
          onValueChange(JSON.stringify(nextRows));
        };
        // Appends to what's on screen, not to `rows` — the untouched first row
        // is unstored, so appending to `rows` would look like the click did nothing.
        const addRow = () => {
          const key = `row-${rowKeySeqRef.current++}`;
          rowKeysRef.current.keys.push(key);
          pendingScrollRowKeyRef.current = key;
          onValueChange(JSON.stringify([...displayRows, {}]));
        };
        const removeRow = (index: number) => {
          rowKeysRef.current.keys.splice(index, 1);
          onValueChange(JSON.stringify(displayRows.filter((_, i) => i !== index)));
        };
        const markNone = () => onValueChange(explicitlyNone ? '' : '[]');

        return (
          <div className={styles.repeatableGroup}>
            <button
              type="button"
              className={`${styles.repeatableNoneBtn} ${explicitlyNone ? styles.repeatableNoneBtnActive : ''}`}
              onClick={markNone}
            >
              <span className={`${styles.checkBox} ${explicitlyNone ? styles.checkBoxSelected : ''}`}>
                {explicitlyNone && <CheckIcon size={14} />}
              </span>
              <span>None</span>
            </button>
            <div className={styles.repeatableRowsScroll}>
              {displayRows.map((row, index) => (
                <div key={rowKeys[index]} data-row-key={rowKeys[index]} className={styles.repeatableRow}>
                  {fields.map((field) => (
                    <div key={field.key} className={styles.repeatableField}>
                      <label className={styles.repeatableFieldLabel}>{field.label}</label>
                      {field.inputType === HtsInputType.SELECT ? (
                        <Select
                          value={row[field.key] || undefined}
                          onChange={(v) => updateRow(index, field.key, v)}
                          placeholder="Choose one…"
                          className={styles.fieldSelect}
                          size="large"
                          options={field.options?.map((o) => ({ value: o, label: o }))}
                        />
                      ) : field.inputType === HtsInputType.YES_NO ? (
                        <div className={styles.yesnoBtns}>
                          <button
                            type="button"
                            className={`${styles.yesnoBtn} ${row[field.key] === HtsYesNoAnswer.YES ? styles.yesnoBtnActive : ''}`}
                            onClick={() => updateRow(index, field.key, HtsYesNoAnswer.YES)}
                          >
                            Yes
                          </button>
                          <button
                            type="button"
                            className={`${styles.yesnoBtn} ${row[field.key] === HtsYesNoAnswer.NO ? styles.yesnoBtnActive : ''}`}
                            onClick={() => updateRow(index, field.key, HtsYesNoAnswer.NO)}
                          >
                            No
                          </button>
                        </div>
                      ) : field.inputType === HtsInputType.CHECKBOXES ? (
                        <div className={styles.checkboxList}>
                          {field.options?.map((opt) => {
                            const selectedOptions = parseCheckboxRowValue(row[field.key]);
                            const selected = selectedOptions.includes(opt);
                            return (
                              <button
                                key={opt}
                                type="button"
                                className={`${styles.checkOpt} ${selected ? styles.checkOptSelected : ''}`}
                                onClick={() =>
                                  updateRow(
                                    index,
                                    field.key,
                                    JSON.stringify(
                                      selected ? selectedOptions.filter((x) => x !== opt) : [...selectedOptions, opt]
                                    )
                                  )
                                }
                              >
                                <span className={`${styles.checkBox} ${selected ? styles.checkBoxSelected : ''}`}>
                                  {selected && <CheckIcon size={14} />}
                                </span>
                                <span>{opt}</span>
                              </button>
                            );
                          })}
                        </div>
                      ) : field.inputType === HtsInputType.DATE ? (
                        <input
                          type="date"
                          value={row[field.key] ?? ''}
                          onChange={(e) => updateRow(index, field.key, e.target.value)}
                          className={styles.fieldDateNative}
                        />
                      ) : (
                        <Input
                          value={row[field.key] ?? ''}
                          onChange={(e) => updateRow(index, field.key, e.target.value)}
                          placeholder={field.label}
                          className={styles.fieldInput}
                          size="large"
                        />
                      )}
                    </div>
                  ))}
                  {rows.length > 0 && (
                    <button
                      type="button"
                      className={styles.repeatableRemoveBtn}
                      onClick={() => removeRow(index)}
                      aria-label="Remove row"
                    >
                      <Trash2 style={{ width: '1em', height: '1em' }} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <button type="button" className={styles.repeatableAddBtn} onClick={addRow}>
              + Add
            </button>
          </div>
        );
      }

      default:
        return null;
    }
  };

  // Plain-text rendering of the saved answer for a locked question — mirrors
  // how an already-answered follow-up round is displayed (read-only, no
  // input control), regardless of which widget type produced the value.
  const renderReadOnlyValue = () => {
    if (question.inputType === HtsInputType.YES_NO) {
      if (answer === HtsYesNoAnswer.YES) return 'Yes';
      if (answer === HtsYesNoAnswer.NO) return 'No';
      return '';
    }
    if (question.inputType === HtsInputType.REPEATABLE_GROUP) {
      const rows = parseGroupRows(answer);
      if (rows.length === 0) return 'None reported';
      const fields = question.groupFields ?? [];
      return rows
        .map((row) =>
          fields
            .map((f) => {
              const raw = row[f.key] ?? '';
              const display = f.inputType === HtsInputType.CHECKBOXES ? parseCheckboxRowValue(raw).join(', ') : raw;
              return `${f.label}: ${display}`;
            })
            .join(' · ')
        )
        .join('\n');
    }
    return Array.isArray(answer) ? answer.join(', ') : answer ?? '';
  };

  const renderField = () => {
    if (locked) {
      return (
        <p className={`${styles.followUpAnsweredText} ${styles.lockedFieldValue}`}>
          {renderReadOnlyValue()}
        </p>
      );
    }
    return (
      <div className={question.inputType === HtsInputType.YES_NO ? styles.yesnoWrap : undefined}>
        {renderFieldFor(answer, onChange)}
      </div>
    );
  };

  return (
    <div ref={glassRef} className={styles.card}>
      <div className={styles.cardEyebrow}>{question.section.toUpperCase()}</div>
      <h2 className={styles.cardTitle}>{question.text}</h2>
      {question.helpText ? (
        <p className={styles.cardHelp}>{question.helpText}</p>
      ) : (
        <div className={styles.cardHelpSpacer} />
      )}

      <div ref={cardBodyRef} className={styles.cardBody}>
        <div className={styles.fieldWrap}>
          {locked && (
            <p className={styles.lockedCaption}>
              {lockedCaption ?? "You've used your one redo — this answer can't be changed again."}
            </p>
          )}
          {!locked && hasSavedAnswer && showEditOnceNote && (
            <p className={styles.editOnceCaption}>You can edit this answer once before it locks.</p>
          )}
          {renderField()}
          {!locked && hasUnsavedChanges && (
            <p className={styles.unsavedCaption}>
              Unsaved changes — will save when you continue.
              {hasSavedAnswer && lastSavedValue !== undefined && (
                <Tooltip title="Revert to saved answer">
                  <button
                    type="button"
                    className={styles.revertBtn}
                    onClick={() => onChange(lastSavedValue)}
                    aria-label="Revert to saved answer"
                  >
                    <RotateCcw size={14} />
                  </button>
                </Tooltip>
              )}
            </p>
          )}
        </div>

        <AnimatePresence>
          {history.length > 0 && (
            <motion.div
              key="followup"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className={styles.followUpWrap}
            >
              {answeredExchanges.map((exchange, i) => (
                <div key={i} className={styles.followUpRound}>
                  <label className={styles.followUpLabel}>{exchange.question}</label>
                  <p className={styles.followUpAnsweredText}>{exchange.answer}</p>
                </div>
              ))}
              {pendingFollowUpQuestion && (
                <div className={styles.followUpRound}>
                  <label className={styles.followUpLabel}>{pendingFollowUpQuestion}</label>
                  {hasUnsavedChanges ? (
                    <p className={styles.editOnceCaption}>
                      Editing your answer above will clear this follow-up — you won't need to answer it.
                    </p>
                  ) : (
                    <Input.TextArea
                      ref={followUpInputRef}
                      value={followUpAnswer ?? ''}
                      onChange={(e) => onFollowUpAnswerChange?.(e.target.value)}
                      placeholder="Type your answer…"
                      className={styles.fieldTextarea}
                      rows={4}
                      maxLength={MAX_ANSWER_MESSAGE_LENGTH}
                    />
                  )}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div className={styles.navRow}>
        {onBack ? (
          <Button appearance="outline" className={styles.navBtn} onClick={onBack}>
            Back
          </Button>
        ) : (
          <div className={styles.navSpacer} />
        )}
        {onBack && <div className={styles.navSpacer} />}
        {isLast ? (
          <Button
            appearance="solid"
            className={styles.navBtn}
            onClick={onSubmit}
            disabled={!allAnswered || blockedOnFollowUp}
            loading={saving}
          >
            Submit
          </Button>
        ) : (
          <Button
            appearance="solid"
            className={styles.navBtn}
            onClick={onNext}
            disabled={blockedOnFollowUp || !currentAnswered}
            loading={saving}
          >
            Next question
          </Button>
        )}
      </div>
    </div>
  );
};
